import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import { CurrencyProvider } from "./lib/currency";
import { get } from "./lib/api";
import { MarketingLayout } from "./components/chrome/Layout";
import { Spinner } from "./components/ui";

import Home from "./pages/Home";
import Pricing from "./pages/Pricing";
import Reviews from "./pages/Reviews";
import Faq from "./pages/Faq";
import Features from "./pages/Features";
import { ChannelsIndex, ChannelDetail } from "./pages/Channels";
import Demo from "./pages/Demo";
import Contact from "./pages/Contact";
import { UseCasesIndex, UseCaseDetail } from "./pages/UseCases";
import { ContentIndex, ContentDetail } from "./pages/Content";
import ToolDetail from "./pages/ToolDetail";
import { IntegrationsDir, IntegrationDetail } from "./pages/Integrations";
import Developers from "./pages/Developers";
import Mcp from "./pages/Mcp";
import Changelog from "./pages/Changelog";
import { Security, Subprocessors, Dpa, Terms, Privacy, RefundPolicy } from "./pages/Legal";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import SeeYou from "./pages/SeeYou";
import TestPay from "./pages/TestPay";
import PayLink from "./pages/PayLink";
import Welcome from "./pages/Welcome";
import NotFound from "./pages/NotFound";
import Styleguide from "./pages/Styleguide";

const AdminApp = lazy(() => import("./pages/admin/AdminApp"));
const ClientDashboard = lazy(() => import("./pages/ClientDashboard"));

const M = (el, cta = true) => <MarketingLayout cta={cta}>{el}</MarketingLayout>;

function Shell() {
  const { data: site } = useQuery({ queryKey: ["site"], queryFn: () => get("/site") });
  const rate = site?.usd_php_rate || 60.8047;
  return (
    <CurrencyProvider rate={rate}>
      <Routes>
        <Route path="/" element={M(<Home />)} />
        <Route path="/features/" element={M(<Features />)} />
        <Route path="/features/channels/" element={M(<ChannelsIndex />)} />
        <Route path="/features/channels/:slug/" element={M(<ChannelDetail />)} />
        <Route path="/pricing/" element={M(<Pricing />)} />
        <Route path="/reviews/" element={M(<Reviews />)} />
        <Route path="/faq/" element={M(<Faq />)} />
        <Route path="/demo/" element={M(<Demo />)} />
        <Route path="/chat" element={M(<Demo embedded />, false)} />
        <Route path="/contact/" element={M(<Contact />)} />
        <Route path="/use-cases/" element={M(<UseCasesIndex />)} />
        <Route path="/use-cases/:slug/" element={M(<UseCaseDetail />)} />

        <Route path="/resources/" element={M(<ContentIndex type="resource" />)} />
        <Route path="/resources/:slug/" element={M(<ContentDetail type="resource" />)} />
        <Route path="/tools/" element={M(<ContentIndex type="tool" />)} />
        <Route path="/tools/:slug/" element={M(<ToolDetail />)} />
        <Route path="/best/" element={M(<ContentIndex type="ranking" />)} />
        <Route path="/best/:slug/" element={M(<ContentDetail type="ranking" />)} />
        <Route path="/alternatives/" element={M(<ContentIndex type="alternative" />)} />
        <Route path="/alternatives/:slug/" element={M(<ContentDetail type="alternative" />)} />

        <Route path="/integrations/" element={M(<IntegrationsDir />)} />
        <Route path="/integrations/:slug/" element={M(<IntegrationDetail />)} />
        <Route path="/developers/" element={M(<Developers />)} />
        <Route path="/developers/mcp/" element={M(<Mcp />)} />
        <Route path="/early-bird" element={<Navigate to="/pricing/" replace />} />
        <Route path="/early-bird/" element={<Navigate to="/pricing/" replace />} />
        <Route path="/early-adopter-lifetime" element={<Navigate to="/pricing/" replace />} />
        <Route path="/early-adopter-lifetime/" element={<Navigate to="/pricing/" replace />} />
        <Route path="/changelog/" element={M(<Changelog />)} />

        <Route path="/security/" element={M(<Security />)} />
        <Route path="/subprocessors/" element={M(<Subprocessors />)} />
        <Route path="/dpa/" element={M(<Dpa />)} />
        <Route path="/terms/" element={M(<Terms />)} />
        <Route path="/terms" element={M(<Terms />)} />
        <Route path="/refund-policy/" element={M(<RefundPolicy />)} />
        <Route path="/refund-policy" element={M(<RefundPolicy />)} />
        <Route path="/privacy-policy/" element={M(<Privacy />)} />
        <Route path="/privacy/" element={M(<Privacy />)} />
        <Route path="/privacy" element={M(<Privacy />)} />

        <Route path="/checkout/success/" element={M(<CheckoutSuccess />, false)} />
        <Route path="/see-you" element={M(<SeeYou />, false)} />
        <Route path="/see-you/" element={M(<SeeYou />, false)} />
        <Route path="/100-test" element={M(<TestPay />, false)} />
        <Route path="/100-test/" element={M(<TestPay />, false)} />
        <Route path="/pay/:token" element={<PayLink />} />
        <Route path="/pay/:token/" element={<PayLink />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/welcome/" element={<Welcome />} />
        <Route path="/styleguide" element={M(<Styleguide />, false)} />
        <Route path="/admin/*" element={<Suspense fallback={<Spinner />}><AdminApp /></Suspense>} />
        <Route path="/dashboard/*" element={<Suspense fallback={<Spinner />}><ClientDashboard /></Suspense>} />
        <Route path="*" element={M(<NotFound />)} />
      </Routes>
    </CurrencyProvider>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </HelmetProvider>
  );
}
